import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import systemAPI from "../../utils/systemAPI";
import { create, all } from "mathjs";

// MathJS instance with custom units/aliases for wet lab
const math = create(all);
try {
  math.createUnit({
    mg: { definition: "1e-3 g", aliases: ["milligram"] },
    g: { definition: "1 g", aliases: ["gram"] },
    kg: {
      definition: "1000 g",
      aliases: ["kilogram", "kg", "kgs", "kilograms"],
    },
    ug: { definition: "1e-6 g", aliases: ["µg", "mcg", "microgram"] },
    ng: { definition: "1e-9 g", aliases: ["nanogram"] },
    l: { definition: "1 l", aliases: ["liter", "litre"] },
    ml: { definition: "1e-3 l", aliases: ["milliliter", "millilitre"] },
    ul: {
      definition: "1e-6 l",
      aliases: ["µl", "mcL", "microliter", "microlitre"],
    },
    nl: { definition: "1e-9 l", aliases: ["nanoliter", "nanolitre"] },
    cl: { definition: "1e-2 l", aliases: ["centiliter", "centilitre"] },
    dl: { definition: "1e-1 l", aliases: ["deciliter", "decilitre"] },
    mol: { definition: "1 mol" },
    mmol: { definition: "1e-3 mol", aliases: ["millimole"] },
    umol: { definition: "1e-6 mol", aliases: ["micromole"] },
    nmol: { definition: "1e-9 mol", aliases: ["nanomole"] },
    pmol: { definition: "1e-12 mol", aliases: ["picomole"] },
    m: { definition: "1 m", aliases: ["meter", "metre"] },
    cm: { definition: "1e-2 m", aliases: ["centimeter", "centimetre"] },
    mm: { definition: "1e-3 m", aliases: ["millimeter", "millimetre"] },
    um: { definition: "1e-6 m", aliases: ["micrometer", "micrometre"] },
    nm: { definition: "1e-9 m", aliases: ["nanometer", "nanometre"] },
    pcs: { definition: "1", aliases: ["unit", "ea", "piece", "pieces"] },
    s: { definition: "1 s", aliases: ["sec", "second"] },
    min: { definition: "60 s", aliases: ["minute"] },
    h: { definition: "3600 s", aliases: ["hr", "hour"] },
    // Add more as needed for Indian labs
  });
} catch (e) {
  /* ignore if already defined */
}

// Helper: Describe a unit safely (must be declared before allSupportedUnits)
const safeDescribe = (unit) => {
  try {
    const u = math.unit(1, unit);
    return { name: u.formatUnits(), base: u.toSI().formatUnits(), unit };
  } catch {
    return null;
  }
};
// Helper: Get all supported units and all aliases as dropdown options
const allSupportedUnits = Array.from(
  new Set([
    ...Object.keys(math.Unit.UNITS),
    "mg",
    "g",
    "kg",
    "ug",
    "ng",
    "l",
    "ml",
    "ul",
    "nl",
    "cl",
    "dl",
    "mol",
    "mmol",
    "umol",
    "nmol",
    "pmol",
    "m",
    "cm",
    "mm",
    "um",
    "nm",
    "pcs",
    "unit",
    "s",
    "min",
    "h",
    // Include common aliases as visible options
    "milligram",
    "gram",
    "kilogram",
    "microgram",
    "nanogram",
    "liter",
    "litre",
    "milliliter",
    "millilitre",
    "microliter",
    "microlitre",
  ])
).filter((u) => {
  // Only include units that mathjs can describe and convert
  try {
    const desc = safeDescribe(u);
    return desc && desc.name && desc.base;
  } catch (err) {
    console.log(`Unit "${u}" cannot be described by mathjs:`, err);
    return false;
  }
});

// For debugging - log all available units
console.log("Available units for dropdown:", allSupportedUnits);

const unitGroups = Array.from(
  new Set(allSupportedUnits.map((u) => safeDescribe(u)?.base).filter(Boolean))
);
const unitsByGroup = unitGroups.reduce((acc, group) => {
  acc[group] = allSupportedUnits.filter((u) => safeDescribe(u)?.base === group);
  return acc;
}, {});

// Preset unit groups for common department types
const departmentUnitPresets = {
  Biotechnology: [
    "g",
    "mg",
    "kg",
    "ug",
    "ng",
    "l",
    "ml",
    "ul",
    "nl",
    "mol",
    "mmol",
    "umol",
    "nmol",
    "pmol",
    "pcs",
    "unit",
  ],
  Pharmacy: [
    "mg",
    "g",
    "kg",
    "ug",
    "ml",
    "l",
    "ul",
    "mmol",
    "mol",
    "pcs",
    "unit",
  ],
  Microbiology: ["ml", "l", "ul", "g", "mg", "ug", "pcs", "unit"],
  Chemistry: [
    "g",
    "mg",
    "kg",
    "ug",
    "ng",
    "l",
    "ml",
    "ul",
    "mol",
    "mmol",
    "nmol",
    "pmol",
    "pcs",
    "unit",
  ],
  Physics: [
    "m",
    "cm",
    "mm",
    "um",
    "nm",
    "kg",
    "g",
    "mg",
    "s",
    "min",
    "h",
    "pcs",
    "unit",
  ],
  General: ["g", "mg", "kg", "ug", "l", "ml", "ul", "pcs", "unit"],
};

// Helper: Get all units by base (e.g., all length, all area, all mass, etc.)
const getUnitsByBase = (base) =>
  allSupportedUnits.filter((u) => safeDescribe(u)?.base === base);

// Helper: Get preset units for selected department
const getPresetUnits = (departments, selectedDepartment) => {
  // Add debug logging to help identify issues
  console.log("Departments:", departments);
  console.log("Selected department ID:", selectedDepartment);

  const dept = departments.find((d) => d.id === selectedDepartment);
  console.log("Found department:", dept);

  if (!dept) return departmentUnitPresets.General;

  // Try exact match first
  if (departmentUnitPresets[dept.name]) {
    console.log(`Found exact preset for: ${dept.name}`);
    return departmentUnitPresets[dept.name];
  }

  // Try case-insensitive match
  const deptNameLower = dept.name.toLowerCase();
  const presetKey = Object.keys(departmentUnitPresets).find(
    (key) => key.toLowerCase() === deptNameLower
  );

  if (presetKey) {
    console.log(
      `Found case-insensitive preset for: ${dept.name} → ${presetKey}`
    );
    return departmentUnitPresets[presetKey];
  }

  // No match found, use General
  console.log(`No preset found for: ${dept.name}, using General`);
  return departmentUnitPresets.General;
};

const DepartmentSettings = () => {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [departmentSettings, setDepartmentSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("category");
  const [dialogValue, setDialogValue] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);

  // Helper: Validate and normalize units
  const normalizeUnits = (input) => {
    // Split by comma, semicolon, or whitespace, trim, and filter out empty
    const rawUnits = input
      .split(/[,;\s]+/)
      .map((u) => u.trim())
      .filter(Boolean);
    // Only keep units that are supported by convert-units
    const validUnits = rawUnits.filter((u) => allSupportedUnits.includes(u));
    const invalidUnits = rawUnits.filter((u) => !allSupportedUnits.includes(u));
    return { validUnits, invalidUnits };
  };

  // Fetch all departments
  useEffect(() => {
    const fetchDepartments = async () => {
      setLoading(true);
      try {
        const res = await systemAPI.getDepartments();
        setDepartments(res.data || []);
        if (res.data && res.data.length > 0) {
          setSelectedDepartment(res.data[0].id);
        }
      } catch (err) {
        setError("Failed to load departments.");
      } finally {
        setLoading(false);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch department settings when department changes
  useEffect(() => {
    if (!selectedDepartment) return;
    setLoading(true);
    setError(null);
    const fetchSettings = async () => {
      try {
        const res = await systemAPI.getDepartmentSettings(selectedDepartment);
        const inventory = res.data?.inventory || {};
        setDepartmentSettings(res.data || {});
        setCategories(inventory.categories || []);
        setUnits(inventory.units || []);
      } catch (err) {
        setError("Failed to load department settings.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [selectedDepartment]);

  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value);
    setSuccess(null);
    setError(null);
  };

  const handleOpenDialog = (type, isEdit = false, item = null) => {
    setDialogType(type);
    setIsEditMode(isEdit);
    setDialogValue(isEdit && item ? item : "");
    setItemToEdit(isEdit && item ? item : null);
    setDialogOpen(true);
  };

  const handleDialogSave = async () => {
    if (!dialogValue && dialogType === "unit") return;
    let updatedList, apiField;
    if (dialogType === "category") {
      // Allow comma/semicolon/whitespace separated categories
      const newCategories = dialogValue
        .split(/[,;\n]+/)
        .map((c) => c.trim())
        .filter((c) => c && !categories.includes(c));
      updatedList = isEditMode
        ? categories.map((cat) => (cat === itemToEdit ? dialogValue : cat))
        : [...categories, ...newCategories];
      setCategories(updatedList);
      apiField = "categories";
    } else {
      // Units: dialogValue is now an array of selected units
      const validUnits = Array.isArray(dialogValue)
        ? dialogValue.filter((u) => allSupportedUnits.includes(u))
        : [];
      if (validUnits.length === 0) {
        setError("Please select at least one valid unit.");
        return;
      }
      updatedList = isEditMode
        ? units.map((unit) => (unit === itemToEdit ? validUnits[0] : unit))
        : [...units, ...validUnits.filter((u) => !units.includes(u))];
      setUnits(updatedList);
      apiField = "units";
    }
    setDialogOpen(false);
    setIsEditMode(false);
    setItemToEdit(null);
    setDialogValue(dialogType === "unit" ? [] : "");
    // Save to backend
    try {
      setLoading(true);
      await systemAPI.updateDepartmentSettings(selectedDepartment, {
        inventory: {
          categories: apiField === "categories" ? updatedList : categories,
          units: apiField === "units" ? updatedList : units,
        },
      });
      setSuccess(
        `${
          dialogType === "category" ? "Category" : "Unit(s)"
        } saved successfully.`
      );
    } catch (err) {
      setError("Failed to save settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, value) => {
    let updatedList;
    if (type === "category") {
      updatedList = categories.filter((cat) => cat !== value);
      setCategories(updatedList);
    } else {
      updatedList = units.filter((unit) => unit !== value);
      setUnits(updatedList);
    }
    // Save to backend
    try {
      setLoading(true);
      await systemAPI.updateDepartmentSettings(selectedDepartment, {
        inventory: {
          categories: type === "category" ? updatedList : categories,
          units: type === "unit" ? updatedList : units,
        },
      });
      setSuccess(`${type === "category" ? "Category" : "Unit"} deleted.`);
    } catch (err) {
      setError("Failed to update settings.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, width: "100%", maxWidth: 900, mx: "auto" }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Department Settings
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Configure categories and units for each department separately.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="department-select-label">Department</InputLabel>
          <Select
            labelId="department-select-label"
            value={selectedDepartment}
            label="Department"
            onChange={handleDepartmentChange}
            disabled={loading || departments.length === 0}
          >
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Divider sx={{ mb: 3 }} />
        {loading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Categories
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ ml: 2 }}
                  onClick={() => handleOpenDialog("category")}
                >
                  Add
                </Button>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {categories.length === 0 ? (
                  <Typography color="text.secondary">
                    No categories defined.
                  </Typography>
                ) : (
                  categories.map((cat) => (
                    <Chip
                      key={cat}
                      label={cat}
                      onDelete={() => handleDelete("category", cat)}
                      onClick={() => handleOpenDialog("category", true, cat)}
                      color="primary"
                    />
                  ))
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Units
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ ml: 2 }}
                  onClick={() => handleOpenDialog("unit")}
                >
                  Add
                </Button>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {units.length === 0 ? (
                  <Typography color="text.secondary">
                    No units defined.
                  </Typography>
                ) : (
                  units.map((unit) => (
                    <Chip
                      key={unit}
                      label={unit}
                      onDelete={() => handleDelete("unit", unit)}
                      onClick={() => handleOpenDialog("unit", true, unit)}
                      color="secondary"
                    />
                  ))
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>
      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditMode ? `Edit ${dialogType}` : `Add ${dialogType}`}
        </DialogTitle>
        <DialogContent
          sx={{ minHeight: dialogType === "unit" ? 400 : undefined }}
        >
          {dialogType === "unit" ? (
            <>
              <Autocomplete
                multiple
                options={unitGroups.flatMap((group) => unitsByGroup[group])}
                groupBy={(option) => safeDescribe(option)?.base || ""}
                getOptionLabel={(option) =>
                  `${option} (${safeDescribe(option)?.name || option})`
                }
                value={Array.isArray(dialogValue) ? dialogValue : []}
                onChange={(_, newValue) => {
                  setDialogValue(newValue);
                  setError(null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Units (multiple allowed)"
                    placeholder="Choose units"
                    helperText={error ? error : undefined}
                    error={!!error}
                  />
                )}
                sx={{ minWidth: 350, minHeight: 300 }}
                disableCloseOnSelect
                popupIcon={null}
                ListboxProps={{ style: { maxHeight: 250 } }}
              />
              {/* Preset quick add for department units */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Quick Add: Department Preset Units
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  {(() => {
                    const presetUnits = getPresetUnits(
                      departments,
                      selectedDepartment
                    );
                    console.log("Preset units before filtering:", presetUnits);
                    const validPresetUnits = presetUnits.filter((u) =>
                      allSupportedUnits.includes(u)
                    );
                    console.log(
                      "Valid preset units after filtering:",
                      validPresetUnits
                    );

                    if (validPresetUnits.length === 0) {
                      return (
                        <Typography color="text.secondary">
                          No preset units available for this department
                        </Typography>
                      );
                    }

                    return validPresetUnits.map((u) => (
                      <Chip
                        key={u}
                        label={`${u} (${safeDescribe(u)?.name || u})`}
                        color={
                          Array.isArray(dialogValue) && dialogValue.includes(u)
                            ? "primary"
                            : "default"
                        }
                        onClick={() => {
                          if (!Array.isArray(dialogValue)) return;
                          if (dialogValue.includes(u)) {
                            setDialogValue(
                              dialogValue.filter((val) => val !== u)
                            );
                          } else {
                            setDialogValue([...dialogValue, u]);
                          }
                          setError(null);
                        }}
                        sx={{ cursor: "pointer" }}
                      />
                    ));
                  })()}
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Quick Add: Select All Units by Type
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {unitGroups.map((base) => (
                    <Chip
                      key={base}
                      label={`All ${base}`}
                      color="info"
                      onClick={() => {
                        const unitsOfBase = getUnitsByBase(base);
                        // Add all units of this base that are not already selected
                        const newUnits = unitsOfBase.filter(
                          (u) => !dialogValue.includes(u)
                        );
                        setDialogValue([...dialogValue, ...newUnits]);
                        setError(null);
                      }}
                      sx={{ cursor: "pointer" }}
                    />
                  ))}
                </Box>
              </Box>
            </>
          ) : (
            <TextField
              autoFocus
              margin="dense"
              label="Category (comma/semicolon/enter separated)"
              type="text"
              fullWidth
              value={dialogValue}
              onChange={(e) => {
                setDialogValue(e.target.value);
                setError(null);
              }}
            />
          )}
          {error && dialogType !== "unit" && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepartmentSettings;
